/**
 * /apps/:appId/setup/hr-policies (and /policies) — Documents, grouped by
 * category (document type, managed in Other Settings).
 *
 * A document is either written in the editor or an uploaded file. Click a row
 * to open it at /apps/:appId/policies/:policyId.
 *
 * "+ New Document" asks for title, category and type. A written document
 * opens in the editor; a file document is uploaded right here.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Modal from '../../components/Modal';
import { useApi } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import { formatDate } from '../../lib/format';
import { useSpacesUpload, validateFiles } from '../../lib/useSpacesUpload';
import {
  DOCUMENT_ACCEPT, categoryColor, documentKind, groupByCategory, useDocumentCategories,
} from '../../components/workspace/documents/documentHelpers';

const STATUS_OPTIONS = [
  { value: 'active',  label: 'Active'  },
  { value: 'draft',   label: 'Draft'   },
  { value: 'retired', label: 'Retired' },
];

export default function Policies() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const { canWrite } = useWorkspace();
  const { categories } = useDocumentCategories(appId);

  const [docs, setDocs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch]     = useState('');
  const [catFilter, setCatFilter]       = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await api.policies.list(appId, { limit: 200 });
      setDocs(r.items || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [api, appId]);

  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = docs.filter(d =>
      (!q || d.title?.toLowerCase().includes(q) || d.file_name?.toLowerCase().includes(q)) &&
      (!catFilter || String(d.category_id) === catFilter) &&
      (!statusFilter || d.status === statusFilter)
    );
    return groupByCategory(list, categories);
  }, [docs, categories, search, catFilter, statusFilter]);

  return (
    <div className="policies-page">
      <div className="entity-head">
        <h1 className="page-title">Documents</h1>
        {canWrite && (
          <button className="btn-primary" onClick={() => setCreateOpen(true)}>
            + New Document
          </button>
        )}
      </div>
      <p className="page-subtitle">
        HR policies, forms, templates and other company documents, grouped by category. Write a document here
        or upload a file. Each has a draft → active → retired lifecycle; employees see Active ones.
        Categories are managed under Other Settings.
      </p>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '4px 0 18px' }}>
        <input className="form-input" placeholder="Search documents…" value={search}
          onChange={e => setSearch(e.target.value)} style={{ maxWidth: 260 }} />
        <select className="tl-select" value={catFilter} onChange={e => setCatFilter(e.target.value)}>
          <option value="">All categories</option>
          {categories.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
        </select>
        <select className="tl-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {error && <div className="banner banner-error">{error}</div>}

      {loading ? (
        <p className="muted">Loading documents…</p>
      ) : docs.length === 0 ? (
        <div className="empty-state-small">
          No documents yet.
          {canWrite && ' Click "+ New Document" to add your first one.'}
        </div>
      ) : groups.length === 0 ? (
        <div className="empty-state-small">No documents match these filters.</div>
      ) : (
        groups.map(g => {
          const color = categoryColor(g.name);
          return (
            <section key={g.id} className="policies-group">
              <h2 className="policies-group-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
                {g.name}
                <span className="muted small"> · {g.docs.length}</span>
              </h2>
              <div className="entity-table-wrap">
                <table className="entity-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th style={{ width: 110 }}>Type</th>
                      <th style={{ width: 100 }}>Status</th>
                      <th style={{ width: 80 }}>Version</th>
                      <th style={{ width: 140 }}>Effective from</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.docs.map(d => (
                      <tr key={d.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/apps/${appId}/policies/${d.id}`)}>
                        <td style={{ fontWeight: 600, whiteSpace: 'normal', wordBreak: 'break-word' }}>{d.title}</td>
                        <td>
                          <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: `${color}18`, color }}>
                            {documentKind(d)}
                          </span>
                        </td>
                        <td><span className={`status-pill status-${d.status}`}>{d.status}</span></td>
                        <td className="muted">v{d.version}</td>
                        <td className="muted">{d.effective_from ? formatDate(d.effective_from) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          );
        })
      )}

      <CreateDocumentModal
        open={createOpen}
        categories={categories}
        onClose={() => setCreateOpen(false)}
        onCreated={(d) => navigate(`/apps/${appId}/policies/${d.id}`)}
      />
    </div>
  );
}

function CreateDocumentModal({ open, categories, onClose, onCreated }) {
  const { appId } = useParams();
  const api = useApi();
  const { uploadFiles } = useSpacesUpload(appId, 'hr_document');
  const [title, setTitle]           = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [docType, setDocType]       = useState('written');
  const [file, setFile]             = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState(null);

  useEffect(() => {
    if (open) {
      setTitle(''); setDocType('written'); setFile(null); setError(null); setSubmitting(false);
      setCategoryId(categories[0] ? String(categories[0].id) : '');
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickFile = (f) => {
    setError(null);
    if (!f) { setFile(null); return; }
    const { errors } = validateFiles([f]);
    if (errors.length) { setError(errors[0]); setFile(null); return; }
    setFile(f);
    if (!title.trim()) setTitle(f.name.replace(/\.[^.]+$/, ''));
  };

  const handle = async (e) => {
    e.preventDefault();
    setError(null);
    if (!title.trim()) { setError('Title is required'); return; }
    if (!categoryId)   { setError('Please choose a category'); return; }
    if (docType === 'file' && !file) { setError('Please choose a file to upload'); return; }
    setSubmitting(true);
    try {
      const body = { title: title.trim(), category_id: Number(categoryId), doc_type: docType };
      if (docType === 'file') {
        const [up] = await uploadFiles([file]);
        Object.assign(body, {
          file_url: up.file_url, file_key: up.file_key, file_name: up.file_name,
          content_type: up.content_type, file_size: up.file_size,
        });
      }
      const created = await api.policies.create(appId, body);
      onCreated?.(created);
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to create');
      setSubmitting(false);
    }
  };

  const typeBtn = (value, label) => (
    <button type="button" onClick={() => setDocType(value)}
      className={docType === value ? 'btn-primary' : 'btn-secondary'}
      style={{ flex: 1 }}>
      {label}
    </button>
  );

  return (
    <Modal open={open} title="New Document" onClose={submitting ? undefined : onClose}>
      <form onSubmit={handle} className="entity-form">
        <label className="form-label">
          <span>Category <span style={{ color: 'var(--status-danger-fg)' }}>*</span></span>
          {categories.length ? (
            <select className="form-input" value={categoryId} onChange={e => setCategoryId(e.target.value)}>
              {categories.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
            </select>
          ) : (
            <small className="form-help">No categories yet. Add one under Organisation Setup → Other Settings.</small>
          )}
        </label>

        <div className="form-label">
          <span>Type</span>
          <div style={{ display: 'flex', gap: 8 }}>
            {typeBtn('written', 'Write it here')}
            {typeBtn('file', 'Upload a file')}
          </div>
        </div>

        {docType === 'file' && (
          <label className="form-label">
            <span>File <span style={{ color: 'var(--status-danger-fg)' }}>*</span></span>
            <input type="file" className="form-input" accept={DOCUMENT_ACCEPT}
              onChange={e => pickFile(e.target.files?.[0])} />
            <small className="form-help">PDF, Word, Excel, PowerPoint, text or image. Max 20 MB.</small>
          </label>
        )}

        <label className="form-label">
          <span>Title <span style={{ color: 'var(--status-danger-fg)' }}>*</span></span>
          <input
            type="text"
            className="form-input"
            placeholder="e.g. Remote Work Policy"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
          />
          <small className="form-help">
            {docType === 'written'
              ? "You'll write the content, status and effective date on the next page."
              : 'Status and effective date can be set on the next page.'}
          </small>
        </label>

        {error && <div className="form-error">{error}</div>}
        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={submitting || !categories.length}>
            {submitting ? (docType === 'file' ? 'Uploading…' : 'Creating…') : docType === 'file' ? 'Upload' : 'Continue to editor'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
